import { useEffect, useState } from "react";
import QrScanner from "qr-scanner";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";

import { API_URL } from "../config/api";
import type { LicencePrefill } from "../models/LicencePrefill";
import { logActivity } from "../services/activity";

type Props = {
  open: boolean;
  onClose: () => void;
  onScanned: (prefill: LicencePrefill) => void;
};

type Status = "scanning" | "loading" | "error";

const LICENCE_ID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export default function ScanLicenseDialog({
  open,
  onClose,
  onScanned,
}: Props) {
  // Ref callback : la vidéo n'existe qu'une fois la Dialog affichée.
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<Status>("scanning");
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);

  const reset = () => {
    setStatus("scanning");
    setMessage("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const retry = () => {
    reset();
    setAttempt((a) => a + 1);
  };

  useEffect(() => {
    if (!video) {
      return;
    }

    let handled = false;

    const scanner = new QrScanner(
      video,
      async (result) => {
        if (handled) {
          return;
        }

        const match = result.data.includes("judowb.be")
          ? result.data.match(LICENCE_ID_RE)
          : null;

        if (!match) {
          setMessage("Ce QR code n'est pas une licence JudoWB.");
          return;
        }

        handled = true;
        scanner.stop();
        setStatus("loading");

        try {
          const response = await fetch(`${API_URL}/licence/${match[0]}`);
          const body = await response.json();

          if (!response.ok) {
            throw new Error(body.error ?? "Lecture de la licence impossible");
          }

          reset();
          logActivity("qr_scan_success");
          onScanned(body);
        } catch (err) {
          const error =
            err instanceof Error
              ? err.message
              : "Lecture de la licence impossible";

          setStatus("error");
          setMessage(error);
          logActivity("qr_scan_failed", { error });
        }
      },
      {
        returnDetailedScanResult: true,
        preferredCamera: "environment",
        highlightScanRegion: true,
        highlightCodeOutline: true,
      }
    );

    logActivity("qr_scan_started");

    scanner.start().catch(() => {
      setStatus("error");
      setMessage(
        "Impossible d'accéder à la caméra. Autorise l'accès à la caméra pour ce site dans ton navigateur, puis réessaie."
      );
      logActivity("qr_scan_failed", { error: "camera" });
    });

    return () => {
      scanner.destroy();
    };
  }, [video, attempt, onScanned]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Scanner une licence
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Vise le QR code au dos de la carte de membre JudoWB.
        </Typography>

        <Box
          sx={{
            position: "relative",
            bgcolor: "black",
            borderRadius: 2,
            overflow: "hidden",
            aspectRatio: "1 / 1",
            maxWidth: "100%",
          }}
        >
          <video
            ref={setVideo}
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: status === "scanning" ? "block" : "none",
            }}
          />

          {status === "loading" && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "common.white",
              }}
            >
              <CircularProgress color="inherit" />
              <Typography>Lecture de la licence...</Typography>
            </Box>
          )}
        </Box>

        {message && (
          <Alert
            severity={status === "error" ? "error" : "warning"}
            sx={{ mt: 2 }}
          >
            {message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {status === "error" && (
          <Button onClick={retry}>
            Réessayer
          </Button>
        )}
        <Button onClick={handleClose}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
